import { Button } from '@/components/ui/button';
import { useBambooRedeemTransactionMutation } from '@/gql/generated';
import useNetwork from '@/hooks/use-network';
import BackSpaceIcon from '@/icons/back-space-icon';
import { InvalidInputError } from '@/lib/error-type';
import { handleError } from '@/lib/handle-error';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatCode, formatNumberToCurrency } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { SoundContext } from '@/pages/(gapsdk)/_provider/sound-provider';
import { cn } from '@/utils/utils';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import RedeemFailed from '../_components/redeem-failed';
import SuccessSection from '../_components/success-section';
import TopNavigation from '../../_components/top-navigation';
import { useAuthenticatedProvider } from '../../../_provider/authenticated-provider';

export default function GiftCardRedeemPage() {
  const { event: eventSound } = useContext(SoundContext);
  const { user } = useAuthenticatedProvider();
  const { isOnline } = useNetwork();
  const whmcsId = user.whmcs_id;
  const navigate = useNavigate();
  const {
    register,
    setValue: setFormValue,
    watch,
    handleSubmit,
  } = useForm({
    defaultValues: {
      value: '',
    },
  });
  const [activeInputValue, setActiveInputValue] = useState('INPUT_VALUE');

  const value = watch('value');

  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const giftCardId = searchParams.get('id');
  const currentBalance = searchParams.get('current_balance');
  const [error, setError] = useState('');

  const handleNumberClick = (num: string) => {
    const newValue = (value || '') + num;

    setFormValue('value', newValue);
  };

  const handleClear = () => {
    setFormValue('value', '');
  };

  const handleDelete = () => {
    if (Number(value) === 0) {
      setFormValue('value', '');
    } else {
      setFormValue('value', value.slice(0, -1));
    }
  };

  const NUMERIC_BUTTONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, 'x'];
  const { event } = useContext(FullScreenLoadingContext);

  const bambooRedeemTransactionMutation = useBambooRedeemTransactionMutation({
    onMutate: () => {
      event.setIsLoading(true);
    },
    onSuccess: () => {
      event.setIsLoading(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onError: (error) => {
      event.setIsLoading(false);
      const errorMessage = handleError({ error });
      if (
        errorMessage instanceof InvalidInputError &&
        errorMessage.errors.length > 0
      ) {
        setError(errorMessage.errors[0]?.message ?? '');
      }
    },
    onSettled: () => {
      event.setIsLoading(false);
    },
  });

  const onSubmit = (data: { value: string }) => {
    bambooRedeemTransactionMutation.mutate({
      input_obj: {
        balance_change: Number(data.value),
        date: formatDateToTimezone(
          new Date().toISOString(),
          user.time_zone!,
          `yyyy-MM-dd'T'HH:mm:ssXXX`,
        ),
        description: '',
        gift_card_id: giftCardId!,
        whmcs_id: Number(whmcsId),
      },
    });
  };

  if (bambooRedeemTransactionMutation.isSuccess) {
    return (
      <SuccessSection
        value={value}
        transaction_id={
          bambooRedeemTransactionMutation.data.bamboo_redemption_transaction.id
        }
      />
    );
  }

  if (bambooRedeemTransactionMutation.isError) {
    return <RedeemFailed errors={error} onBack={() => navigate(-1)} />;
  }

  return (
    <div className="flex h-full flex-col items-center justify-center p-24">
      <TopNavigation onBack={() => navigate(-1)} />
      <div className="mx-auto size-full">
        <div className="grid size-full grid-cols-12 gap-12">
          <div className="col-span-7">
            <form onSubmit={handleSubmit(onSubmit)} className="size-full">
              <div className="flex size-full flex-col">
                <div className="flex-none">
                  <div className="mb-10">
                    <div className="mb-5 text-2xl font-normal">
                      Redemption amount
                    </div>
                    <div
                      {...register('value')}
                      onClick={() => setActiveInputValue('INPUT_VALUE')}
                      className={cn(
                        'h-24 rounded-lg border-2 border-[#E3E3E333] bg-gray-50 text-5xl font-bold',
                        {
                          'border-primary': activeInputValue === 'INPUT_VALUE',
                        },
                      )}
                    >
                      <div
                        className={cn(
                          'flex size-full items-center px-8 text-gray-400',
                          { 'text-black': value },
                        )}
                      >
                        {value ? formatNumberToCurrency(Number(value)) : '$0'}
                      </div>
                    </div>
                    {
                      <div className="pt-1 text-danger-discount">
                        {Number(value) < 100 && value ? (
                          <div className="h-1 w-full">Minimum value is $1</div>
                        ) : (
                          <div className="h-1 w-full"></div>
                        )}
                        {Number(value) > Number(currentBalance) && value ? (
                          <div className="h-1 w-full">
                            The total charge cannot be greater than the gift
                            card amount
                          </div>
                        ) : (
                          <div className="h-1 w-full"></div>
                        )}
                        <div className="text-danger-discount">
                          {Number(value) > 100000000 &&
                          Number(value) <= Number(currentBalance) ? (
                            'The maximum value is $1,000,000.00.'
                          ) : (
                            <div className="h-1 w-full"></div>
                          )}
                        </div>
                      </div>
                    }
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-normal text-gray-700">
                      Gift card
                    </div>
                    <div className="text-2xl font-bold">
                      {formatCode(code ?? '')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-normal">Current balance</div>
                    <div className="text-2xl font-bold">
                      {formatNumberToCurrency(Number(currentBalance!))}
                    </div>
                  </div>
                </div>

                <div className="grow" />
                <div className="flex-none">
                  <Button
                    type="submit"
                    className="click-effect mt-6 h-24 w-full rounded-lg text-2xl font-bold"
                    disabled={
                      !value ||
                      Number(value) < 100 ||
                      Number(value) > Number(currentBalance) ||
                      Number(value) > 100000000 ||
                      !isOnline
                    }
                    onClick={() => eventSound.playTap()}
                  >
                    Redeem
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <div className="col-span-5 grid h-full grid-cols-3 gap-2">
            {NUMERIC_BUTTONS.map((num, i) => (
              <button
                key={i}
                className={
                  'click-effect flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white text-5xl font-bold'
                }
                onMouseDown={() => {
                  eventSound.playTap();
                  if (num === 'C') {
                    handleClear();
                    return;
                  }
                  if (num === 'x') {
                    handleDelete();
                    return;
                  }
                  handleNumberClick(num.toString());
                }}
              >
                {num === 'x' ? (
                  <div className="flex items-center justify-center">
                    <BackSpaceIcon
                      style={{
                        width: 48,
                        height: 48,
                      }}
                    />
                  </div>
                ) : (
                  num
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
